import React, { useCallback, useEffect, useState } from 'react';
import { SharedTable } from '../../components/sharedTable';
import { Box, Button, Typography } from '@mui/material';
import EditIcon from '@mui/icons-material/Edit';
import { IconButton } from '@mui/material';
import { GridColDef, GridFilterItem, GridPaginationModel, GridRenderCellParams, GridSortItem } from '@mui/x-data-grid-pro';
import { useAppDispatch } from '../../store';
import { useSelector } from 'react-redux';
import { selectIsStockEmployeesLoading, selectStockEmployees, selectTotalRowStockEmployees } from '../../store/selectors/stockEmployeesSelectors';
import { getStockEmployees } from '../../store/slices/stockEmployeesSlice';
import { useNavigate } from 'react-router-dom';
import { ToastContainer } from 'react-toastify';
import 'react-toastify/dist/ReactToastify.css';
import { getStoredItem, storeItem } from '../../services/storageService';
import { getStateSnapshotFromLocalStorage } from '../../utils/state';

const tableName = 'stockEmployees';
const defaultSorting: GridSortItem[] = [{ field: 'id', sort: 'desc' }];
const defaultPagination: Partial<GridPaginationModel> = { page: 0, pageSize: 25 };
const defaultFilter: GridFilterItem[] = [
  {
    field: 'active',
    operator: 'equals',
    value: true,
  },
];

export const StockEmployeesPage: React.FC = () => {
  const dispatch = useAppDispatch();
  const navigate = useNavigate();

  const employees = useSelector(selectStockEmployees);
  const isEmployeesLoading = useSelector(selectIsStockEmployeesLoading);
  const totalRows = useSelector(selectTotalRowStockEmployees);

  const [isDataNededToBeFetched, setIsDataNededToBeFetched] = useState(true);
  const [isPaginationChange, setIsPaginationChange] = useState(false);
  const [selectedRowIds, setSelectedRowIds] = useState<number[]>([]);

  const dispatchGetEmployees = useCallback(async () => {
    const snapshotState = await getStateSnapshotFromLocalStorage(tableName);
    const resultAction = await dispatch(
      getStockEmployees({
        filtering: snapshotState?.filter?.filterModel?.items ?? defaultFilter,
        sorting: snapshotState?.sorting?.sortModel ?? defaultSorting,
        ...(snapshotState?.pagination?.paginationModel ?? defaultPagination),
      })
    );

    if (getStockEmployees.fulfilled.match(resultAction)) {
      setIsDataNededToBeFetched(false);
      const storedSelectedRowIds = getStoredItem<number[]>('selectedOn' + tableName);
      if (storedSelectedRowIds) {
        setSelectedRowIds(storedSelectedRowIds);
      }
    }
  }, [dispatch]);

  const columns: GridColDef[] = [
    {
      field: 'id',
      headerName: 'ID',
      type: 'number',
    },
    {
      field: 'name',
      headerName: 'ФИО',
      type: 'string',
      flex: 1,
    },
    {
      field: 'warehouse',
      headerName: 'Склад',
      type: 'string',
      flex: 1,
    },
    {
      field: 'active',
      headerName: 'Активен',
      type: 'boolean',
    },
    {
      field: 'actions',
      headerName: 'Редактировать',
      renderCell: (params: GridRenderCellParams) => (
        <IconButton color="primary" onClick={() => navigate(`/stock-employees/edit/${params.row.name}`)}>
          <EditIcon />
        </IconButton>
      ),
    },
  ];

  const handleSelectionChange = useCallback(
    (pageSelectedRowIds: number[]) => {
      if (isPaginationChange) {
        setIsPaginationChange(false);
        return;
      }
      setSelectedRowIds(pageSelectedRowIds);
      storeItem('selectedOn' + tableName, pageSelectedRowIds);
    },
    [selectedRowIds, isPaginationChange]
  );

  const handlePaginationChange = useCallback(() => {
    setIsDataNededToBeFetched(true);
    setIsPaginationChange(true);
  }, []);

  const handleDataQueryChange = useCallback(() => {
    setIsDataNededToBeFetched(true);
  }, []);

  useEffect(() => {
    if (isDataNededToBeFetched) {
      dispatchGetEmployees();
    }
  }, [isDataNededToBeFetched]);

  return (
    <div>
      <ToastContainer />
      <Box
        sx={{
          display: 'flex',
          justifyContent: 'space-between',
          alignItems: 'center',
          padding: '24px',
        }}
      >
        <Typography variant="h4">Сотрудники склада</Typography>
        <Box sx={{ display: 'flex', gap: '12px' }}>
          <Button variant="contained" color="primary" onClick={() => navigate('/stock-employees/create')}>
            Добавить
          </Button>
        </Box>
      </Box>
      <SharedTable
        columns={columns}
        loading={isEmployeesLoading}
        disableColumnSelector={true}
        onFilterModelChange={handleDataQueryChange}
        onPaginationChange={handlePaginationChange}
        onSelectionChange={handleSelectionChange}
        onSortModelChange={handleDataQueryChange}
        rowCount={totalRows}
        rows={employees}
        selectedRowIds={selectedRowIds}
        tableName={tableName}
      />
    </div>
  );
};
